import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Log } from 'oidc-client';
import { IDSAuthService } from 'src/app/services/idsauth.service';
import { TokenService } from 'src/app/services/token.service';
import { UserService } from '../../usermanagement/services/user.service';
import { UserProfileService } from '../../usermanagement/services/user-profile.service';
import { ViewUserProfileResponse } from '../../usermanagement/models/user.model';

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [],
  templateUrl: './callback.component.html',
  styles: ``
})
export class CallbackComponent implements OnInit {
  constructor(
    private idsauthService: IDSAuthService,
    private router: Router,
    private tokenService: TokenService,
    private userProfileService: UserProfileService,
    private userService: UserService,
  ) { }

  ids_loggedin_token: any = null;

  ngOnInit(): void {
    // Clean up old localStorage key with trailing space (migration)
    localStorage.removeItem('ids_loggedin_token ');

    let isLoggedIn = this.idsauthService.getLogedInUserIDSData();
    isLoggedIn
      .subscribe((isLoggedIn) => {
        console.log('CallbackComponent: isLoggedIn data received', isLoggedIn);
        this.ids_loggedin_token = isLoggedIn;
        // Use cached session only if the access token is still valid (not expired)
        if (isLoggedIn != undefined && isLoggedIn != null && this.isAccessTokenValid(isLoggedIn.access_token)) {
          this.tokenService.setToken(isLoggedIn?.access_token);
          // User is authenticated via OIDC, try to extract email and fetch profile
          let email = '';
          let viewProfile = localStorage.getItem('viewProfile');
          if (viewProfile) {
            const parsedProfile: ViewUserProfileResponse = JSON.parse(viewProfile);
            this.userProfileService.setUserProfile(parsedProfile);
            email = parsedProfile.email;
          }
          if (email == '' || email == null) {
            email = this.extractEmailFromToken();
          }
          if (email && this.ids_loggedin_token) {
            this.idsauthService.userDetails = email;
            this.userService.getUserProfileByEmail(email).subscribe({
              next: (viewProfile: ViewUserProfileResponse) => {
                this.userProfileService.setUserProfile(viewProfile);
                this.navigateToUserDashboard();
              },
              error: (error) => {
                console.error('Error fetching user profile from API:', error);
                // Navigate anyway so the user is not stuck on /callback
                this.navigateToUserDashboard();
              }
            });
          } else {
            // No email available — retry after a short delay then navigate
            setTimeout(() => {
              const retryProfile = this.userProfileService.getCurrentUserProfile();
              if (retryProfile) {
                this.userProfileService.setUserProfile(retryProfile);
              }
            }, 1000);
            this.navigateToUserDashboard();
          }
        }
        else {
          // Cached user is absent or has an expired token — process the fresh IDS redirect
          // to obtain a new valid access token
          Log.logger = console;
          let endSigninMainWindow = this.idsauthService.endSigninMainWindow();
          endSigninMainWindow
            .subscribe((user) => {
              console.log('CallbackComponent: user data received', user);
              if (user == null) {
                this.router.navigate(['/login'])
              }
              else {
                console.log('CallbackComponent: user data', user);
                this.tokenService.setToken(user.access_token);
                let email = user.profile.email || user.profile.sub || user.profile.preferred_username || '';
                this.idsauthService.userDetails = email;
                this.userService.getUserProfileByEmail(email).subscribe({
                  next: (viewProfile: ViewUserProfileResponse) => {
                    // Store the profile in the service and localStorage
                    this.userProfileService.setUserProfile(viewProfile);
                    this.navigateToUserDashboard();
                  },
                  error: (error) => {
                    console.error('Error fetching user profile from API:', error);
                    // Error handling is now managed by the JWT interceptor
                  }
                });
              }
            });
        }

      });
    const bodyTag = document.getElementsByTagName('body')[0];
    bodyTag.classList.add('welcome-page');
  }

  /**
   * Validates an access token string directly (without reading from localStorage).
   * Used to detect expired cached tokens returned by the OIDC client due to clockSkew.
   */
  private isAccessTokenValid(token: string | undefined): boolean {
    if (!token) return false;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(atob(parts[1]));
      if (payload.exp) {
        return payload.exp > Math.floor(Date.now() / 1000);
      }
      return true;
    } catch {
      return false;
    }
  }


  private extractEmailFromToken(): string | '' {
    try {
      if (!this.ids_loggedin_token) {
        return '';
      }
      // Try to decode JWT token from various possible properties
      let token = '';

      // Check common JWT token property names
      if (this.ids_loggedin_token.access_token) {
        token = this.ids_loggedin_token.access_token;
      } else if (this.ids_loggedin_token.accessToken) {
        token = this.ids_loggedin_token.accessToken;
      } else if (this.ids_loggedin_token.id_token) {
        token = this.ids_loggedin_token.id_token;
      } else if (typeof this.ids_loggedin_token === 'string') {
        // If ids_loggedin_token  itself is the token string
        token = this.ids_loggedin_token;
      }

      if (token && typeof token === 'string') {
        // Decode JWT token payload
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));

          // Try different email claim names
          return payload.email ||
            payload.sub ||
            payload.unique_name ||
            payload.preferred_username ||
            payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ||
            payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
            '';
        }
      }
      return '';
    } catch (error) {
      console.error('Error extracting email from token:', error);
      return '';
    }
  }

  private navigateToUserDashboard(): void {
    setTimeout(() => {
      const savedRoute = localStorage.getItem('resend_route');
      // Always clear before navigating — prevents the route from being reused
      localStorage.removeItem('resend_route');

      // Never navigate back to /callback or /login: that would restart this whole
      // flow and create an infinite redirect loop.
      const isSafeRoute = savedRoute
        && !savedRoute.startsWith('/callback')
        && !savedRoute.startsWith('/login');

      if (isSafeRoute) {
        this.router.navigate([savedRoute], { replaceUrl: true });
      } else {
        this.router.navigateByUrl('/', { replaceUrl: true });
      }
    }, 50);
  }

}
